import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import {
  createKonbinezonValidationRule, deleteKonbinezonValidationRule,
  listKonbinezonValidationRules, updateKonbinezonValidationRule,
} from '../../api/konbinezonValidation';
import './KonbinezonRatioPage.css';

// RÈG 2 — Validation Konbinezon (L3, Mariaj, L41-43, L51-53 — meme ensemble que
// RÈG 1). Quand ON, sur toute fiche qui porte >= 1 ligne konbinezon :
//  1. chaque ligne doit se décomposer en boule(s) déjà jouées en Borlette sur la
//     fiche (2 boules pour MAR/L4x/L5x, 1 seule pour L3 — pas de combinaison
//     sans borlette) ;
//  2. Σ mises konbinezon >= Σ mises BOR × P (toujours, pas de seuil).
// Portée company (Tout) / group / bank — le plus spécifique gagne côté vente.

const PCT_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 6); // 6 .. 15
const DEFAULTS = { enabled: false, min_pct: 6 };

const scopeParams = (filter, scopeId) => {
  if (filter === 'Groupe') return { group: scopeId };
  if (filter === 'Banque') return { bank: scopeId };
  return {};
};

const KonbinezonValidationPage = () => {
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');

  const [ruleId, setRuleId] = useState(null);
  const [enabled, setEnabled] = useState(DEFAULTS.enabled);
  const [minPct, setMinPct] = useState(DEFAULTS.min_pct);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const scopeReady = filter === 'Tout' || !!scopeId;

  const load = useCallback(() => {
    if (!scopeReady) return;
    setLoading(true);
    setError('');
    setSaved(false);
    listKonbinezonValidationRules(scopeParams(filter, scopeId))
      .then((rows) => {
        const rule = Array.isArray(rows) ? rows[0] : null;
        if (rule) {
          setRuleId(rule.id);
          setEnabled(rule.enabled);
          setMinPct(Math.round(Number(rule.min_pct)));
        } else {
          setRuleId(null);
          setEnabled(DEFAULTS.enabled);
          setMinPct(DEFAULTS.min_pct);
        }
      })
      .catch(() => setError('Impossible de charger la règle.'))
      .finally(() => setLoading(false));
  }, [filter, scopeId, scopeReady]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    const payload = { enabled, min_pct: minPct };
    try {
      if (ruleId) {
        await updateKonbinezonValidationRule(ruleId, payload);
      } else {
        const created = await createKonbinezonValidationRule({ ...payload, ...scopeParams(filter, scopeId) });
        setRuleId(created.id);
      }
      setSaved(true);
    } catch (err) {
      setError('Sauvegarde impossible. Vérifiez les valeurs.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!ruleId) return;
    setSaving(true);
    setError('');
    try {
      await deleteKonbinezonValidationRule(ruleId);
      setRuleId(null);
      setEnabled(DEFAULTS.enabled);
      setMinPct(DEFAULTS.min_pct);
      setSaved(false);
    } catch (err) {
      setError('Suppression impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <p className="kr-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/securite" className="bc-link">Securite</Link>
        {' >> '}
        <span className="bc-current">Validation Konbinezon</span>
      </p>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">Validation Konbinezon (RÈG 2)</span>
        </div>
        <div className="kr-card-body">
          <div className="kr-radio-row">
            {['Tout', 'Groupe', 'Banque'].map((opt) => (
              <label key={opt} className="kr-radio-label">
                <input
                  type="radio"
                  name="kv-filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="kr-row">
              <label className="kr-label">{filter}</label>
              {filter === 'Banque' ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="kr-input kr-input-sm"
                  iconClass="kr-icon-btn"
                  nameClass="kr-scope-name"
                  errClass="kr-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="kr-input kr-input-sm"
                  iconClass="kr-icon-btn"
                  nameClass="kr-scope-name"
                  errClass="kr-lookup-err"
                />
              )}
            </div>
          )}

        </div>
      </div>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">
            Règle {filter === 'Tout' ? 'générale (company)' : filter.toLowerCase()}
          </span>
        </div>

        {!scopeReady ? (
          <div className="kr-card-body">
            <p className="kr-hint">Choisir un {filter.toLowerCase()} ci-dessus.</p>
          </div>
        ) : loading ? (
          <div className="kr-card-body"><p>Chargement...</p></div>
        ) : (
          <form className="kr-card-body" onSubmit={handleSave}>
            <label className="kr-toggle-row">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => { setEnabled(e.target.checked); setSaved(false); }}
              />
              <span>Aktive règ la</span>
              {!ruleId && <span className="kr-hint">(pas encore de règle pour cette portée)</span>}
            </label>

            <div className="kr-row">
              <label className="kr-label">Pousantaj min.</label>
              <select
                className="kr-input"
                value={minPct}
                disabled={!enabled}
                onChange={(e) => { setMinPct(Number(e.target.value)); setSaved(false); }}
              >
                {PCT_OPTIONS.map((p) => <option key={p} value={p}>{p} %</option>)}
              </select>
              <span className="kr-hint">Σ Konbinezon ≥ Σ mis BOR × sa a</span>
            </div>

            <div className="kr-btn-row">
              <button type="submit" className="kr-save-btn" disabled={saving}>
                {ruleId ? 'Anrejistre' : 'Kreye règ la'}
              </button>
              {ruleId && (
                <button type="button" className="kr-del-btn" onClick={handleDelete} disabled={saving}>
                  Efase
                </button>
              )}
            </div>

            {saved && <p className="kr-ok">Konfigirasyon anrejistre.</p>}
            {error && <p className="kr-error">{error}</p>}
          </form>
        )}
      </div>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">Kijan sa mache</span>
        </div>
        <div className="kr-card-body">
          <p className="kr-explain">
            Lè règ la ON, sou chak fich ki gen omwen yon <strong>konbinezon</strong>
            (L3, Mariaj, L41 rive L43, L51 rive L53) :
          </p>
          <p className="kr-explain">
            1. Chak konbinezon dwe fèt ak boul ki <strong>deja jwe an Borlette sou menm fich la</strong>.
            Egzanp fich ak 20, 30, 34, 50 (Mariaj/L41/L42/L43) : 20×30 ✅, 20×34 ✅, 30×50 ✅, men 20×70 ❌
            (70 pa jwe). Pou L51/L52, 2 boul yo se 2 premye ak 2 dènye chif yo ; pou L53, 2 boul yo
            se chif 2-3 ak chif 4-5 (premye chif la pa konte) ; pou L3 (3 chif), se 1 sèl boul,
            2 dènye chif yo. Pa gen konbinezon san borlet.
          </p>
          <p className="kr-explain">
            2. Total mis nan tout konbinezon yo (L3, Mariaj, L41-L53) dwe <strong>≥ {minPct} %</strong>
            {' '}total mis BOR yo (toujou, depi gen yon konbinezon — pa gen sèy sou kantite boul).
          </p>
          <p className="kr-explain kr-explain-note">
            Si youn nan de kondisyon yo pa respekte → fich la bloke. Règ 2 endepandan
            de Règ 1 (Ratio Konbinezon) : de yo ka aktive an menm tan.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default KonbinezonValidationPage;
